import { useTranslation } from 'react-i18next';
import { useMask } from '@react-input/mask';
import styled from 'styled-components';

import countries from 'assets/constants/countries';
import states from 'assets/constants/states';
import { theme } from 'assets/styles/theme';
import RedButton from 'components/buttons/RedButton';
import Checkbox from 'components/inputs/Checkbox';
import DropdownField from 'components/inputs/DropdownField';
import TextInputField from 'components/inputs/TextInputField';
import { H4 } from 'components/typography/Headers';
import { Text1 } from 'components/typography/Texts';

import { OrganizationFormData, useAddOrganizationForm } from './useAddOrganizationForm';

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-width: 500px;
  gap: 24px;
  min-height: 200px;
  max-height: 75vh;
  text-align: center;
`;

const Header = styled(H4)`
  margin-bottom: 12px;

  ${theme.mq.phone} {
    margin-bottom: 0;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 280px;
  overflow-y: auto;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const StyledCheckbox = styled(Checkbox)`
  padding-top: 8px;
  align-self: start;
`;

const Button = styled(RedButton)`
  height: 46px;
`;

interface Props {
  organization?: OrganizationFormData;
  onSelected: (organization: OrganizationFormData) => void;
}

const AddOrganization = ({ organization, onSelected }: Props) => {
  const { t } = useTranslation('myProfile');
  const { values, setFieldValue, errors, isValid } = useAddOrganizationForm(onSelected, organization);

  const einInputRef = useMask({ mask: '_________', replacement: { _: /\d/ } });

  return (
    <Container data-testid={'organization-add'}>
      <Header data-testid={'header'}>{t('become-fundraiser.verify-organization.add-organization-header')}</Header>
      <Text1 data-testid={'description'}>
        {t('become-fundraiser.verify-organization.add-organization-description')}
      </Text1>
      <FormContainer data-testid={'shipping-address-form'}>
        <TextInputField
          value={values.name}
          onChange={value => setFieldValue('name', value)}
          placeholder={t('form-inputs.organization-name-placeholder')}
          errorMessage={errors.name}
        />
        <FormRow>
          <TextInputField
            value={values.ein}
            inputRef={einInputRef}
            onChange={value => setFieldValue('ein', value)}
            placeholder={t('form-inputs.ein-placeholder')}
            errorMessage={errors.ein}
          />
          <StyledCheckbox
            checked={values.isNonProfit}
            testId={'nonprofit-field'}
            label={t('form-inputs.non-profit-label')}
            onChange={value => setFieldValue('isNonProfit', value)}
          />
        </FormRow>
        <FormRow>
          <TextInputField
            value={values.addressLine1}
            onChange={value => setFieldValue('addressLine1', value)}
            placeholder={t('form-inputs.address-placeholder')}
            errorMessage={errors.addressLine1}
          />
          <TextInputField
            value={values.addressLine2 || ''}
            onChange={value => setFieldValue('addressLine2', value)}
            placeholder={t('form-inputs.address-optional-placeholder')}
            errorMessage={errors.addressLine2}
          />
          <TextInputField
            value={values.city}
            onChange={value => setFieldValue('city', value)}
            placeholder={t('form-inputs.city-placeholder')}
            errorMessage={errors.city}
          />
        </FormRow>
        <FormRow>
          <DropdownField
            testId={'state-field'}
            value={values.state}
            options={states}
            placeholder={t('form-inputs.state-placeholder')}
            onChange={value => setFieldValue('state', value)}
            errorMessage={errors.state}
          />
          <TextInputField
            value={values.zipCode}
            onChange={value => setFieldValue('zipCode', value)}
            placeholder={t('form-inputs.zip-placeholder')}
            errorMessage={errors.zipCode}
          />
          <DropdownField
            testId={'country-field'}
            value={values.country}
            options={countries}
            placeholder={t('form-inputs.country-placeholder')}
            onChange={value => setFieldValue('country', value)}
            errorMessage={errors.country}
          />
        </FormRow>
      </FormContainer>
      <Button onClick={() => onSelected(values)} disabled={!isValid}>
        {t('common:next')}
      </Button>
    </Container>
  );
};

export default AddOrganization;
